import {
  makeAutoObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { navigate } from 'gatsby';
import { AttachmentType, CPDCategory, HomepageAttachment, PublicPageImageLanguageCategory, PAGE_SIZE, PlatformType, VmList, VmListT, HomepageSectionCategory } from '../constants/options';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { USER_INFO } from '../constants/storage-keys';
import { FAxios } from '../utilities/file-network';
import { isDevelopment } from '../constants/settings';
import { HomepageFaq, HomepageFaqReq } from '../constants/homepage';

export default class PublicStore {

  loading: boolean = false;
  loadingInfo: boolean = false;

  uploading: boolean = false;
  deletingFile: boolean = false;
  downloading: boolean = false;

  uploadingPublicDownloadDocument: boolean = false;
  savingPublicDownloadDocument: boolean = false;

  loadingHomepageAttachments: boolean = false;
  updatingAttachmentIds: Set<number> = new Set<number>();
  uploadingHomepageAttachment: boolean = false;
  deletingHomepageAttachment: boolean = false;
  homepageAttachments: HomepageAttachment[] = [];

  loadingFaqList: boolean = false;
  loadingFaq: boolean = false;
  savingFaq: boolean = false;
  deletingFaq: boolean = false;
  reorderingFaq: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.rootStore = rootStore;
  }

  searchPublicDownloads = async (skip: number, take: number, reqModel: {
    downloadCategoryId?: number,
    description?: string,
    searchTerm?: string,
    sortBy?: "LastModifiedAt" | "SeqNo"
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Download/Attachment/Search/${skip}/${take}`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.PUBLIC_DOWNLOAD,
        sortBy: reqModel.sortBy || "LastModifiedAt"
      });

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  uploadPublicDownloadDocument = async (reqModel: {
    fileName: string,
    attachmentCategoryId: number,
    file: string,
    downloadCategoryId: number,
    description: string
  }) => {
    this.uploadingPublicDownloadDocument = true;
    try {
      const res = await Axios.post(`/api/Download/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingPublicDownloadDocument = false;
    }
  };

  updatePublicDownloadDocument = async (reqModel: {
    downloadCategoryId: number,
    attachmentCategoryId: number,
    description: string,
    originalName: string,
    id: number,
    isPublic: boolean
  }) => {
    this.savingPublicDownloadDocument = true;
    try {
      const res = await Axios.put(`/api/Download/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPublicDownloadDocument = false;
    }
  };

  deletePublicDownloadDocument = async (id: number) => {
    this.deletingFile = true;
    try {
      const res = await Axios.delete(`/api/Download/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingFile = false;
    }
  }

  downloadPublicDownload = async (savedName: string) => {
    this.downloading = true;
    try {
      const res = await Axios.get(`/api/Download/Attachment/${savedName}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  }

  updatePublicDownloadOrder = async (files: Array<{ id?: number; seqNo: number }>) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Download/Attachment/Seq`, files);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  // HOMEPAGE ATTACHMENTS
  searchHomepageAttachments = async (skip: number, take: number, reqModel: {
    languageCategoryIds?: PublicPageImageLanguageCategory[],
    homepageCategoryId: HomepageSectionCategory,
    summitCpdId?: number,
  }) => {
    this.loadingHomepageAttachments = true;
    try {
      const res = await Axios.post<VmListT<HomepageAttachment>>(`/api/Homepage/Attachment/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHomepageAttachments = false;
    }
  };

  uploadHomepageAttachment = async (reqModel: {
    fileName: string,
    file: string, // Base64 part
    languageId: PublicPageImageLanguageCategory,
    homepageCategoryId: number,
    summitCpdId?: number,
    description?: string
  }) => {
    this.uploadingHomepageAttachment = true;
    try {
      const res = await Axios.post(`/api/Homepage/Attachment`, {
        ...reqModel,
        isPublic: true,
        attachmentCategoryId: AttachmentType.PUBLIC_HOMEPAGE_IMAGES,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingHomepageAttachment = false;
    }
  };

  deleteHomepageAttachment = async (id: number) => {
    this.deletingHomepageAttachment = true;
    try {
      const res = await Axios.delete(`/api/Homepage/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingHomepageAttachment = false;
    }
  };

  updateHomepageAttachmentOrder = async (items: { id: number, seqNo: number }[]) => {
    this.loadingHomepageAttachments = true;
    try {
      const res = await Axios.put(`/api/Homepage/Attachment/Seq`, items);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingHomepageAttachments = false;
    }
  };

  updateHomepageAttachment = async (reqModel: {
    languageId: PublicPageImageLanguageCategory,
    homepageCategoryId: number,
    originalName: string,
    id: number,
    description?: string
  }) => {
    this.savingPublicDownloadDocument = true;
    this.updatingAttachmentIds.add(reqModel.id);
    try {
      const res = await Axios.put(`/api/Homepage/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.PUBLIC_HOMEPAGE_IMAGES,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPublicDownloadDocument = false;
      this.updatingAttachmentIds.delete(reqModel.id);
    }
  };

  // HOMEPAGE FAQ
  searchHomepageFaq = async (skip: number, take: number, reqModel: {
    homepageCategoryId?: HomepageSectionCategory,
    searchTerm?: string,
  }) => {
    this.loadingFaqList = true;
    try {
      const res = await Axios.post<VmListT<HomepageFaq>>(`/api/Homepage/Faq/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingFaqList = false;
    }
  };

  getHomepageFaq = async (id: number) => {
    this.loadingFaq = true;
    try {
      const res = await Axios.get<HomepageFaq>(`/api/Homepage/Faq/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingFaq = false;
    }
  };

  createHomepageFaq = async (reqModel: HomepageFaqReq) => {
    this.savingFaq = true;
    try {
      const res = await Axios.post<HomepageFaq>(`/api/Homepage/Faq`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingFaq = false;
    }
  };

  updateHomepageFaq = async (reqModel: HomepageFaqReq) => {
    this.savingFaq = true;
    try {
      const res = await Axios.put<HomepageFaq>(`/api/Homepage/Faq`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingFaq = false;
    }
  };

  deleteHomepageFaq = async (id: number) => {
    this.deletingFaq = true;
    try {
      const res = await Axios.delete(`/api/Homepage/Faq/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingFaq = false;
    }
  };

  updateHomepageFaqOrder = async (items: { id: number, seqNo: number }[]) => {
    this.reorderingFaq = true;
    try {
      const res = await Axios.put(`/api/Homepage/Faq/Sort`, items);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.reorderingFaq = false;
    }
  };
}


